import { HttpBackend, HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BASE_URL } from '@models/global-constant.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/** Standard backend envelope — `data` carries the payload, `message` is written for the user. */
export interface ApiEnvelope<T> {
  status: boolean;
  message?: string;
  code?: string;
  data: T;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

/** Drop null/undefined/'' so an omitted `project_id` means "personal calendar" rather than "". */
function toHttpParams(params?: QueryParams): HttpParams {
  let httpParams = new HttpParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    httpParams = httpParams.set(key, String(value));
  }
  return httpParams;
}

function unwrap<T>() {
  return map((res: ApiEnvelope<T>) => res?.data as T);
}

/** The parsed failure envelope, whichever transport surfaced it. */
function errorEnvelope(err: unknown): Partial<ApiEnvelope<unknown>> | null {
  if (err instanceof HttpErrorResponse) {
    return (err.error as Partial<ApiEnvelope<unknown>>) ?? null;
  }
  if (err && typeof err === 'object') {
    return err as Partial<ApiEnvelope<unknown>>;
  }
  return null;
}

/**
 * The message the backend wrote for this failure.
 *
 * A 4xx `message` is always phrased for the user (`"Monday: invalid time window (the end must be
 * after the start)"`), so it is shown as-is rather than mapped to local copy — only a transport
 * failure with no envelope falls back.
 */
export function calendarApiMessage(err: unknown, fallback: string): string {
  const message = errorEnvelope(err)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

/** Machine-readable failure code, e.g. `agent_selection_required` on a 400 availability response. */
export function calendarApiCode(err: unknown): string {
  const code = errorEnvelope(err)?.code;
  return typeof code === 'string' ? code : '';
}

/** Payload carried alongside a failure (the bookable-agent list on `agent_selection_required`). */
export function calendarApiData<T>(err: unknown): T | null {
  return (errorEnvelope(err)?.data as T) ?? null;
}

export function calendarApiStatus(err: unknown): number {
  return err instanceof HttpErrorResponse ? err.status : 0;
}

/**
 * Thin transport for `/api/v1/calendar/*`.
 *
 * Two clients on purpose: the default one runs through the app interceptors (bearer token,
 * 401 handling), while `public*` calls go straight to `HttpBackend` so the anonymous buyer on
 * `/book/...` never sends an Authorization header — the same pattern as
 * `lead-booking-public-api.service.ts`. Every method unwraps the envelope, so callers see the
 * payload and failures arrive as the raw `HttpErrorResponse` for {@link calendarApiMessage}.
 */
@Injectable({ providedIn: 'root' })
export class CalendarHttpService {
  private readonly http = inject(HttpClient);
  private readonly publicHttp: HttpClient;

  private readonly root = `${BASE_URL}/calendar`;

  constructor(backend: HttpBackend) {
    this.publicHttp = new HttpClient(backend);
  }

  get<T>(path: string, params?: QueryParams): Observable<T> {
    return this.http.get<ApiEnvelope<T>>(this.root + path, { params: toHttpParams(params) }).pipe(unwrap<T>());
  }

  post<T>(path: string, body?: unknown): Observable<T> {
    return this.http.post<ApiEnvelope<T>>(this.root + path, body ?? {}).pipe(unwrap<T>());
  }

  put<T>(path: string, body?: unknown): Observable<T> {
    return this.http.put<ApiEnvelope<T>>(this.root + path, body ?? {}).pipe(unwrap<T>());
  }

  patch<T>(path: string, body?: unknown): Observable<T> {
    return this.http.patch<ApiEnvelope<T>>(this.root + path, body ?? {}).pipe(unwrap<T>());
  }

  delete<T>(path: string): Observable<T> {
    return this.http.delete<ApiEnvelope<T>>(this.root + path).pipe(unwrap<T>());
  }

  /** Anonymous read under `/calendar/public` — never sends a token. */
  publicGet<T>(path: string, params?: QueryParams): Observable<T> {
    return this.publicHttp.get<ApiEnvelope<T>>(`${this.root}/public${path}`, { params: toHttpParams(params) }).pipe(unwrap<T>());
  }

  /** Anonymous write under `/calendar/public` — never sends a token. */
  publicPost<T>(path: string, body?: unknown): Observable<T> {
    return this.publicHttp.post<ApiEnvelope<T>>(`${this.root}/public${path}`, body ?? {}).pipe(unwrap<T>());
  }
}
